/**
 * Builds the "Auth Information" fields rendered on the submission status screen.
 *
 * Fields whose data source is not yet wired (payer portal, draft creation date)
 * are mocked here and can be replaced once the source is available.
 *
 * @module buildSubmissionAuthInfo
 */
import moment from "moment";
import {
  AuthOnFile,
  GeneralOrderDetails,
  SingleCoverage,
} from "../../../data-model/medicalPaOrdersModel";

/** A single label/value pair in the Auth Information grid. */
export interface AuthInfoField {
  label: string;
  value: string;
  /** When true, renders a copy-to-clipboard affordance next to the value. */
  copyable?: boolean;
}

const FALLBACK = "--";

/**
 * Collects the authorization numbers of every J-code across the order's auth
 * entries and joins them into a single comma-separated string.
 *
 * The Postgres `/order-auth` payload exposes auth ranges under `entries`;
 * `auth_entries` is the legacy fallback. Blank auth numbers are skipped and
 * duplicates are removed so the "ID" field stays readable.
 *
 * @input authOnFile - Postgres auth-on-file data (`authOnFileDetails.auth_on_file`).
 * @returns Comma-separated J-code auth numbers, or the `--` fallback when none exist.
 */
const collectJCodeAuthIds = (authOnFile?: AuthOnFile): string => {
  const authEntries = authOnFile?.entries ?? authOnFile?.auth_entries ?? [];
  const authIds = authEntries
    .flatMap((entry) => entry?.j_codes ?? [])
    .map((jCode) => jCode?.auth_number?.trim())
    .filter((authNumber): authNumber is string => Boolean(authNumber));

  const uniqueAuthIds = Array.from(new Set(authIds));
  return uniqueAuthIds.length > 0 ? uniqueAuthIds.join(", ") : FALLBACK;
};

/**
 * Builds the ordered Auth Information fields for the 3-column grid.
 *
 * The order is row-major across 3 columns so the layout matches the design:
 * row 1 = ID, Payer Portal, Draft Creation Date; row 2 = Plan Name, Member ID, Provider.
 *
 * @input order - Postgres general-order details (`generalOrderDetails.order`).
 * @input primaryCoverage - Primary coverage row from `coverageDetails`.
 * @input authOnFile - Postgres auth-on-file data used to derive the J-code auth ids.
 * @returns Ordered fields for the Auth Information grid.
 */
export const buildSubmissionAuthInfoFields = (
  order?: GeneralOrderDetails,
  primaryCoverage?: SingleCoverage,
  authOnFile?: AuthOnFile,
): AuthInfoField[] => {
  const provider = order?.payload?.practitioner_name?.trim() || FALLBACK;
  const planName = primaryCoverage?.payer_name?.trim() || FALLBACK;
  const memberId = primaryCoverage?.member_id?.trim() || FALLBACK;
  const orderCreatedAt = order?.timestamps?.order_created_at;
  const draftDate = orderCreatedAt
    ? moment(orderCreatedAt).format("MM/DD/YYYY")
    : FALLBACK;
  const payerPortal = primaryCoverage?.payer_name?.trim() || FALLBACK;
  const authIds = collectJCodeAuthIds(authOnFile);

  return [
    { label: "ID", value: authIds, copyable: true },
    { label: "Payer Portal", value: payerPortal },
    { label: "Draft Creation Date", value: draftDate },
    { label: "Plan Name", value: planName, copyable: true },
    { label: "Member ID", value: memberId },
    { label: "Provider", value: provider },
  ];
};
